import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Subject, of, throwError } from 'rxjs';
import { Injectable } from '@angular/core';
import { ConfigurationService } from 'app/core/services/configuration.service';
import { NotifMessageService } from './notifmessage.service';
import { catchError, map } from 'rxjs/operators';
import { User } from '../models/user';
import { Roles } from '../models/roles';
import { UserPager } from '../models/pagers/user-pager';

@Injectable({ providedIn: 'root' })
export class UserService {

    constructor(
        private http: HttpClient,
        private configService: ConfigurationService,
        private notifService: NotifMessageService
    ) { }

    userSubject = new Subject<any[]>();
    criteriaSubject = new Subject<any[]>();
    notifySubject = new Subject<any[]>();

    emitUsers(users) {
        this.userSubject.next(users);
    }

    emitCriteria(criteria) {
        this.criteriaSubject.next(criteria);
    }

    notifyEmitNeeded() {
        this.notifySubject.next();
    }

    getUsers(pager: UserPager): Observable<Array<User>> {
        let params = {};
        Object.assign(params, pager);
        return this.http.get<any>(this.configService.config.urlAPI.urlUser, {params: params}).pipe(catchError(err => {
            console.log("error", err);
            this.notifService.notifyErrorWithDetailFromApi('ERROR_USER_API', err);
            let emptyList = new Array<User>();
            return of(emptyList);
        }));
    }

    getUserById(id): Observable<User> {
        return this.http.get<any>(`${this.configService.config.urlAPI.urlUser}/${id}`).pipe(catchError(err => {
            console.log("error", err);
            this.notifService.notifyErrorWithDetailFromApi('ERROR_USER_API', err);
            let emptyItem = new User();
            return of(emptyItem);
        }));
    }

    getUserByUsername(username, ignoreNotFoundError?: boolean): Observable<Array<User>> {
        return this.http.get<any>(`${this.configService.config.urlAPI.urlUser}/username?username=${username}`).pipe(catchError(err => {
            if (!(err.status == 404 && ignoreNotFoundError)) {
                console.log("error", err);
                this.notifService.notifyErrorWithDetailFromApi('ERROR_USER_API', err);
            }
            return of([]);
        }));
    }

    getUserByEmail(email): Observable<Array<User>> {
        return this.http.get<any>(`${this.configService.config.urlAPI.urlUser}?search=${email}`).pipe(catchError(err => {
            console.log("error", err);
            this.notifService.notifyErrorWithDetailFromApi('ERROR_USER_API', err);
            return of([]);
        }));
    }

    createUser(user): Observable<any> {
        return this.http.post<User>(`${this.configService.config.urlAPI.urlUser}`, user).pipe(catchError(err => {
            console.log("error", err);
            this.notifService.notifyErrorWithDetailFromApi('ERROR_USER_API', err);
            return throwError(err);
        }));
    }

    addRoles(user, roles): Observable<any> {
        return this.http.post<any>(`${this.configService.config.urlAPI.urlUser}/${user.id}/roles/role-mappings`, roles).pipe(catchError(err => {
            console.log("error", err);
            this.notifService.notifyErrorWithDetailFromApi('ERROR_USER_API', err);
            return of(null);
        }));
    }

    removeRoles(user, roles): Observable<any> {
        const httpOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }), body: roles };
        return this.http.delete<any>(`${this.configService.config.urlAPI.urlUser}/${user.id}/roles/role-mappings`, httpOptions).pipe(catchError(err => {
            console.log("error", err);
            this.notifService.notifyErrorWithDetailFromApi('ERROR_USER_API', err);
            return of(null);
        }));
    }

    getRoles(user): Observable<Array<Roles>> {
        return this.http.get<any>(`${this.configService.config.urlAPI.urlUser}/${user.id}/roles`).pipe(catchError(err => {
            console.log("error", err);
            this.notifService.notifyErrorWithDetailFromApi('ERROR_USER_API', err);
            let emptyList = new Array<Roles>();
            return of(emptyList);
        }));
    }

    getAllRoles(): Observable<Array<Roles>> {
        return this.http.get<any>(`${this.configService.config.urlAPI.urlRole}`).pipe(catchError(err => {
            console.log("error", err);
            this.notifService.notifyErrorWithDetailFromApi('ERROR_USER_API', err);
            let emptyList = new Array<Roles>();
            return of(emptyList);
        }));
    }

    getUsersByRole(role): Observable<Array<User>> {
        return this.http.get<any>(`${this.configService.config.urlAPI.urlRole}/${role}/users`).pipe(catchError(err => {
            console.log("error", err);
            this.notifService.notifyErrorWithDetailFromApi('ERROR_USER_API', err);
            let emptyList = new Array<User>();
            return of(emptyList);
        }));
    }

    updateUser(user): Observable<any> {
        return this.http.put<User>(`${this.configService.config.urlAPI.urlUser}/${user.id}`, user).pipe(catchError(err => {
            console.log("error", err);
            this.notifService.notifyErrorWithDetailFromApi('ERROR_USER_API', err);
            return throwError(err);
        }));
    }

    deleteUser(id): Observable<any> {
        return this.http.delete<User>(`${this.configService.config.urlAPI.urlUser}/${id}`).pipe(catchError(err => {
            console.log("error", err);
            this.notifService.notifyErrorWithDetailFromApi('ERROR_USER_API', err);
            return of("error"); // return "error" because the function on success return null
        }));
    }

    updatePassword(user): Observable<any> {
        const httpOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };
        let url = `${this.configService.config.security.keycloak.authserver}/admin/realms/${this.configService.config.security.keycloak.realm}/users/${user.id}/execute-actions-email?client_id=${this.configService.config.security.keycloak.clientId}&redirect_uri=/`;
        return this.http.put<any>(url, ["UPDATE_PASSWORD"]).pipe(catchError(err => {
            console.log("error", err);
            this.notifService.notifyErrorWithDetailFromApi('ERROR_USER_API', err);
            return of(null);
        }));
    }

    resetPassword(user): Observable<any> {
        const httpOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };
        return this.http.put<any>(`${this.configService.config.security.keycloak.authserver}/admin/realms/${this.configService.config.security.keycloak.realm}/users/${user.id}/reset-password`, { type: "password", temporary: false, value: "my-new-password" }, httpOptions).pipe(catchError(err => {
            console.log("error", err);
            this.notifService.notifyErrorWithDetailFromApi('ERROR_USER_API', err);
            return of(null);
        }));
    }

    isUsernameExists(username: string): Observable<boolean> {
        return this.getUserByUsername(username, true).pipe(map(res => res.length > 0));
    }

    isEmailExists(email: string, userId?: string): Observable<boolean> {
        return this.getUserByEmail(email).pipe(map(res => {
            if (userId) {
                res = res.filter(elt => elt.id != userId);

            }
            return res.length > 0;
        }));
    }
}